<template>
  <div class="overflow-x-clip bg-neutral-50">
    <Head v-if="article" :title="article.title" />

    <div v-if="article" class="mx-auto w-full min-w-0 max-w-[1200px] px-4 py-8 sm:px-6 lg:px-0">
      <!-- Breadcrumb: Strona główna / Aktualności / Tytuł wpisu -->
      <nav class="mb-8 flex min-w-0 flex-wrap items-center gap-1 text-sm" aria-label="Breadcrumb">
        <Link :href="homeHref" class="shrink-0 text-neutral-700 hover:text-[#2ea8cf]">
          {{ $t('breadcrumbs.home') }}
        </Link>
        <span class="shrink-0 text-neutral-300" aria-hidden="true">/</span>
        <Link :href="articlesIndexHref" class="shrink-0 text-neutral-700 hover:text-[#2ea8cf]">
          {{ $t('breadcrumbs.articles') }}
        </Link>
        <span class="shrink-0 text-neutral-300" aria-hidden="true">/</span>
        <span class="min-w-0 break-words text-[#2ea8cf]">{{ article.title }}</span>
      </nav>

      <!-- Two columns: main content 2/3 (left) and sticky cover image 1/3 (right). Single column on mobile. -->
      <div class="grid min-w-0 grid-cols-1 gap-8 lg:grid-cols-[2fr_1fr] lg:items-start lg:gap-8">
        <article class="flex min-w-0 flex-col gap-8">
          <!-- Header: badge + title -->
          <header class="flex min-w-0 flex-col gap-3">
            <neutral-pill
              v-if="site_context_label"
              :label="site_context_label"
            />
            <h1 class="min-w-0 break-words text-[32px] font-medium leading-10 tracking-[-0.02em] lg:text-[40px] lg:leading-[48px]">
              {{ article.title }}
            </h1>

            <!-- Mobile cover image: under the title, hidden on desktop -->
            <div
              v-if="article.cover_image"
              class="mt-1 w-full overflow-hidden rounded-xl bg-neutral-200 lg:hidden"
            >
              <img
                :src="article.cover_image"
                :alt="article.title"
                class="aspect-[16/10] h-full w-full object-cover"
              >
            </div>

            <time
              v-if="article.published_at"
              :datetime="article.published_at"
              class="text-sm leading-5 text-neutral-500"
            >
              {{ formatDate(article.published_at) }}
            </time>
          </header>

          <!-- Intro / excerpt (Figma: introductory paragraph, 18px) -->
          <p
            v-if="article.excerpt"
            class="max-w-[800px] break-words text-lg leading-7 text-neutral-700"
          >
            {{ article.excerpt }}
          </p>

          <!-- Divider -->
          <hr class="h-px w-full border-0 bg-neutral-200">

          <!-- body_html sanitized server-side by HtmlSanitizer -->
          <div class="article-prose min-w-0 max-w-none overflow-x-hidden" v-html="article.body_html" />
        </article>

        <!-- Sidebar cover image: sticky on the aside itself (same pattern as SpecialistShow). -->
        <aside
          v-if="article.cover_image"
          class="hidden min-w-0 lg:block lg:sticky lg:self-start transition-[top] duration-300 ease-out"
          :style="{ top: `${stickyPanelTop}px` }"
        >
          <div class="overflow-hidden rounded-xl bg-neutral-200">
            <img
              :src="article.cover_image"
              :alt="article.title"
              class="aspect-[16/10] h-full w-full object-cover"
            >
          </div>
        </aside>
      </div>
    </div>

    <div v-else class="mx-auto max-w-[1200px] px-4 py-16 text-center text-neutral-500">
      {{ $t('articles.not_found') }}
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/vue3';
import NeutralPill from '@/Shared/NeutralPill.vue';
import { stickyPanelTop } from '@/utils/stickyMainHeader';

export default {
  name: 'ArticleShow',
  components: { Head, Link, NeutralPill },
  props: {
    article:            { type: Object, default: null },
    // eslint-disable-next-line vue/prop-name-casing
    site_context:       { type: String, default: null },
    // eslint-disable-next-line vue/prop-name-casing
    site_context_label: { type: String, default: '' },
  },
  computed: {
    stickyPanelTop() {
      return stickyPanelTop.value;
    },
    effectiveSiteContext() {
      return this.article?.site_context || this.site_context || 'code_medical_center';
    },
    homeHref() {
      const ctx = this.effectiveSiteContext;
      if (ctx && ctx !== 'code_medical_center' && this.$routes[ctx]) {
        return this.$routes[ctx]();
      }
      return '/';
    },
    articlesIndexHref() {
      const ctx = this.effectiveSiteContext;
      const routeName = `${ctx}_articles`;
      if (ctx && ctx !== 'code_medical_center' && this.$routes[routeName]) {
        return this.$routes[routeName]();
      }
      return this.$routes.articles_index();
    },
  },
  methods: {
    formatDate(iso) {
      try {
        return new Date(iso).toLocaleDateString('pl-PL', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        });
      } catch {
        return iso;
      }
    },
  },
};
</script>

<style scoped>
/* Figma "Wpis - desktop": typography for rich-text body (TipTap output) */
.article-prose {
  max-width: 100%;
  overflow-wrap: break-word;
  word-break: break-word;
}

.article-prose :deep(p) {
  margin-bottom: 1.25rem;
  font-size: 1.125rem;
  line-height: 1.75rem;
  color: #374151;
}

.article-prose :deep(p:empty),
.article-prose :deep(p:has(> br:only-child)) {
  min-height: 1.75rem;
}

.article-prose :deep(h1) {
  margin-top: 2rem;
  margin-bottom: 1rem;
  font-size: 1.75rem;
  font-weight: 500;
  line-height: 2.25rem;
  letter-spacing: -0.02em;
  color: #111827;
}

.article-prose :deep(h2) {
  margin-top: 2rem;
  margin-bottom: 1rem;
  font-size: 1.5rem;
  font-weight: 500;
  line-height: 2rem;
  letter-spacing: -0.02em;
  color: #111827;
}

.article-prose :deep(h3) {
  margin-top: 1.5rem;
  margin-bottom: 0.75rem;
  font-size: 1.375rem;
  font-weight: 500;
  line-height: 1.875rem;
  letter-spacing: -0.02em;
  color: #111827;
}

.article-prose :deep(h4) {
  margin-top: 1.25rem;
  margin-bottom: 0.625rem;
  font-size: 1.25rem;
  font-weight: 500;
  line-height: 1.75rem;
  letter-spacing: -0.02em;
  color: #111827;
}

.article-prose :deep(h5),
.article-prose :deep(h6) {
  margin-top: 1.25rem;
  margin-bottom: 0.5rem;
  font-size: 1.125rem;
  font-weight: 500;
  line-height: 1.625rem;
  color: #111827;
}

@media (min-width: 1024px) {
  .article-prose :deep(h1) {
    margin-top: 2.5rem;
    margin-bottom: 1.25rem;
    font-size: 2.25rem;
    line-height: 2.75rem;
  }

  .article-prose :deep(h2) {
    margin-top: 2.5rem;
    margin-bottom: 1.25rem;
    font-size: 1.875rem;
    line-height: 2.375rem;
  }

  .article-prose :deep(h3) {
    margin-top: 2rem;
    margin-bottom: 1rem;
    font-size: 2rem;
    line-height: 2.5rem;
  }

  .article-prose :deep(h4) {
    margin-top: 1.5rem;
    margin-bottom: 0.75rem;
    font-size: 1.75rem;
    line-height: 2.25rem;
  }

  .article-prose :deep(h5),
  .article-prose :deep(h6) {
    font-size: 1.25rem;
    line-height: 1.75rem;
  }
}

.article-prose :deep(a) {
  color: #0369a1;
  text-decoration: underline;
  text-underline-offset: 0.12em;
  text-decoration-thickness: 2px;
}

.article-prose :deep(a:has(span[style*="color"])) {
  text-decoration: none;
}

.article-prose :deep(a span[style*="color"]),
.article-prose :deep(a[style*="color"]) {
  text-decoration: underline;
  text-decoration-color: currentColor;
  text-underline-offset: 0.12em;
  text-decoration-thickness: 2px;
}

.article-prose :deep(ul),
.article-prose :deep(ol) {
  margin-bottom: 1.25rem;
  padding-left: 1.5rem;
  font-size: 1.125rem;
  line-height: 1.75rem;
  color: #374151;
}

.article-prose :deep(li) {
  margin-bottom: 0.5rem;
}

.article-prose :deep(blockquote) {
  margin: 1.5rem 0;
  border-left: 4px solid #e5e7eb;
  padding-left: 1.25rem;
  font-size: 1.125rem;
  font-style: italic;
  line-height: 1.75rem;
  color: #374151;
}

/* Skip images that set their own size/alignment via rt-img-* classes. */
.article-prose :deep(img:not([class*="rt-img-"])) {
  margin: 1.5rem 0;
  width: 100%;
  max-width: 100%;
}

.article-prose :deep(code) {
  overflow-wrap: anywhere;
  word-break: break-word;
}

.article-prose :deep(pre) {
  max-width: 100%;
  overflow-x: auto;
  border-radius: 0.5rem;
  background: #f3f4f6;
  padding: 1rem;
}

.article-prose :deep(pre code) {
  white-space: pre-wrap;
}

.article-prose :deep(table) {
  display: block;
  max-width: 100%;
  overflow-x: auto;
}

.article-prose :deep(strong:not(span[style*="color"] *)) {
  font-weight: 700;
  color: #111827;
}

.article-prose :deep(span[style*="color"] :is(strong, em, u, s, code)) {
  color: inherit;
}

@media (max-width: 1023px) {
  .article-prose :deep(img[class*="rt-img-"]) {
    float: none !important;
    display: block;
    width: 100% !important;
    max-width: 100% !important;
    margin: 1rem 0 !important;
  }
}
</style>
